import React from 'react';
import { Link } from 'react-router-dom';
import BlogLayout from '../blog/BlogLayout';
import SEO from '../components/SEO';
import { BreadcrumbStructuredData } from '../components/StructuredData';
import { findPost } from '../resources/postLookup';
import { AREAS, QUESTION_COUNT, score } from './migrationReadiness';
import type { Answer } from './migrationReadiness';

const SITE_URL = 'https://www.greyfielddata.com';
const URL_PATH = '/tools/sigma-migration-readiness';
const BOOKING_URL = 'https://cal.com/linustse/meeting-with-linus?overlayCalendar=true';

const OPTIONS: { value: Answer; label: string }[] = [
  { value: 'yes', label: 'Yes' },
  { value: 'partly', label: 'Partly' },
  { value: 'no', label: 'No' },
];

export default function MigrationReadinessPage() {
  const [answers, setAnswers] = React.useState<Record<string, Answer>>({});
  const result = score(answers);
  const remaining = QUESTION_COUNT - result.answered;
  const guides = Array.from(new Set(result.gaps.map((gap) => gap.guide)))
    .map((slug) => ({ slug, post: findPost(slug) }))
    .filter((entry) => entry.post);

  return (
    <BlogLayout wide>
      <SEO
        title="Sigma migration readiness check"
        description="Sixteen questions to see whether you are ready to scope a Tableau or Power BI migration to Sigma, and which gaps to close first."
        url={`${SITE_URL}${URL_PATH}`}
      />
      <BreadcrumbStructuredData
        items={[
          { name: 'Home', url: SITE_URL },
          { name: 'Resources', url: `${SITE_URL}/resources` },
          { name: 'Sigma migration readiness check', url: `${SITE_URL}${URL_PATH}` },
        ]}
      />

      <nav aria-label="Breadcrumb" className="text-sm text-slate-500">
        <Link to="/resources" className="hover:text-[#1a4e94]">Resources</Link>
        <span aria-hidden="true" className="mx-2">/</span>
        <span className="text-slate-700">Sigma migration readiness check</span>
      </nav>

      <header className="mt-5 max-w-3xl">
        <h1 className="text-4xl font-semibold leading-tight tracking-tight text-slate-950 sm:text-5xl">Sigma migration readiness check</h1>
        <p className="mt-5 text-lg leading-8 text-slate-600">Moving from Tableau or Power BI? Answer {QUESTION_COUNT} questions to see what you already know, what is missing, and which guide covers each gap. Your answers are not sent anywhere.</p>
      </header>

      <div className="mt-10 grid gap-8 lg:grid-cols-[minmax(0,1fr)_380px]">
        <div className="space-y-8">
          {AREAS.map((area, areaIndex) => (
            <fieldset key={area.id} className="rounded-2xl border border-slate-200 p-5 sm:p-7">
              <legend className="px-2 text-lg font-semibold text-slate-950">{areaIndex + 1}. {area.name}</legend>
              <ol className="space-y-6">
                {area.questions.map((question) => (
                  <li key={question.id}>
                    <p id={`q-${question.id}`} className="font-medium leading-7 text-slate-900">{question.text}</p>
                    <div role="radiogroup" aria-labelledby={`q-${question.id}`} className="mt-3 flex flex-wrap gap-2">
                      {OPTIONS.map((option) => {
                        const checked = answers[question.id] === option.value;
                        return (
                          <label key={option.value} className={`inline-flex min-h-[44px] cursor-pointer items-center rounded-xl border px-4 py-2 text-sm font-semibold transition focus-within:ring-2 focus-within:ring-blue-200 ${checked ? 'border-[#1a4e94] bg-[#1a4e94] text-white' : 'border-slate-300 bg-white text-slate-700 hover:border-[#1a4e94]'}`}>
                            <input
                              type="radio"
                              name={question.id}
                              value={option.value}
                              checked={checked}
                              onChange={() => setAnswers((current) => ({ ...current, [question.id]: option.value }))}
                              className="sr-only"
                            />
                            {option.label}
                          </label>
                        );
                      })}
                    </div>
                  </li>
                ))}
              </ol>
            </fieldset>
          ))}
        </div>

        <aside className="lg:sticky lg:top-28 lg:self-start">
          <div className="rounded-3xl bg-[radial-gradient(circle_at_top_right,_#18356d_0,_#0a192f_45%,_#071525_100%)] p-7 text-white">
            {result.band ? (
              <>
                <p role="status" className="text-sm font-semibold uppercase tracking-wide text-blue-200">Readiness {result.percent}%<span className="sr-only">: {result.band.label}</span></p>
                <h2 className="mt-3 text-2xl font-semibold">{result.band.label}</h2>
                <p className="mt-3 text-sm leading-6 text-slate-300">{result.band.body}</p>
              </>
            ) : (
              <>
                <p className="text-sm font-semibold uppercase tracking-wide text-blue-200">{result.answered} of {QUESTION_COUNT} answered</p>
                <h2 className="mt-3 text-2xl font-semibold">{result.answered === 0 ? 'Start with the first question' : `${remaining} to go`}</h2>
                <p className="mt-3 text-sm leading-6 text-slate-300">Your result appears when every question has an answer. Gaps are listed as you go.</p>
              </>
            )}
            <dl className="mt-6 space-y-3 border-t border-white/10 pt-5 text-sm">
              {result.areas.map((area) => (
                <div key={area.id}>
                  <div className="flex justify-between gap-4">
                    <dt className="text-slate-300">{area.name}</dt>
                    <dd className="tabular-nums">{area.answered === area.total ? `${area.percent}%` : `${area.answered}/${area.total}`}</dd>
                  </div>
                  <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-white/10" aria-hidden="true">
                    <div className="h-full rounded-full bg-blue-400" style={{ width: `${area.answered === area.total ? area.percent : 0}%` }} />
                  </div>
                </div>
              ))}
            </dl>
            {result.answered > 0 && (
              <button type="button" onClick={() => setAnswers({})} className="mt-6 text-sm font-semibold text-blue-200 underline underline-offset-4 hover:text-white">Start over</button>
            )}
          </div>

          {guides.length > 0 && (
            <div className="mt-6 rounded-2xl border border-slate-200 p-6">
              <h2 className="font-semibold text-slate-950">Read next for your gaps</h2>
              <ul className="mt-3 space-y-2 text-sm">
                {guides.map(({ slug, post }) => (
                  <li key={slug}><Link to={`/blog/${slug}`} className="text-[#1a4e94] underline underline-offset-4 hover:text-slate-950">{post?.title}</Link></li>
                ))}
              </ul>
            </div>
          )}
        </aside>
      </div>

      {result.gaps.length > 0 && (
        <section className="mt-14 border-t border-slate-200 pt-12">
          <h2 className="text-2xl font-semibold text-slate-950">Your gaps</h2>
          <p className="mt-2 text-slate-600">Each item you answered with Partly or No, in order.</p>
          <ul className="mt-6 divide-y divide-slate-200 border-y border-slate-200">
            {result.gaps.map((gap) => (
              <li key={gap.id} className="grid gap-2 py-4 sm:grid-cols-[120px_minmax(0,1fr)_auto] sm:items-baseline sm:gap-6">
                <span className={`text-xs font-semibold uppercase tracking-wide ${gap.answer === 'no' ? 'text-amber-700' : 'text-slate-500'}`}>{gap.area}: {gap.answer === 'no' ? 'No' : 'Partly'}</span>
                <span className="text-slate-800">{gap.text}</span>
                <Link to={`/blog/${gap.guide}`} className="text-sm font-semibold text-[#1a4e94] underline underline-offset-4">Guide</Link>
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mt-14 grid gap-8 rounded-3xl bg-slate-50 p-7 sm:p-10 lg:grid-cols-[1fr_auto] lg:items-center">
        <div>
          <h2 className="text-2xl font-semibold text-slate-950">Want this as a worksheet?</h2>
          <p className="mt-2 max-w-2xl leading-7 text-slate-600">The assessment checklist has room for owners, dates, and notes. Fill it in with your team, then bring it to a scoping call.</p>
          <div className="mt-5 flex flex-wrap gap-5">
            <a href="/resources/sigma-migration-checklist.md" download className="font-semibold text-[#1a4e94] underline underline-offset-4">Download the checklist (Markdown)</a>
            <Link to="/resources/sigma" className="font-semibold text-[#1a4e94] underline underline-offset-4">All Sigma guides</Link>
            <Link to="/services/tableau-to-sigma" className="font-semibold text-[#1a4e94] underline underline-offset-4">Tableau to Sigma</Link>
            <Link to="/services/power-bi-to-sigma" className="font-semibold text-[#1a4e94] underline underline-offset-4">Power BI to Sigma</Link>
          </div>
        </div>
        <a href={BOOKING_URL} target="_blank" rel="noopener noreferrer" className="inline-flex justify-center rounded-xl bg-[#1a4e94] px-6 py-3 font-semibold text-white hover:bg-blue-800">Discuss your migration</a>
      </section>
    </BlogLayout>
  );
}
